import { useCallback } from "react";
import { MarketingTag } from "@/bryntum-lib/models/MarketingTag";
import { AssetThumbnail } from "@/react/components/entityViews/marketing/components/MarketingAssetsContent";
import { useMarketingAssets } from "@/react/components/entityViews/marketing/hooks/useMarketingAssets";
import { useFileDetailModal } from "../../../context/FileDetailModalContext";
import { usePromotePanel, PROMOTE_STEPS } from "../context/PromotePanelContext";
import { PromoteStepHelpTrigger } from "./PromoteStepHelpTrigger";
import { MarketingFolderSelector } from "./MarketingFolderSelector";

export const OrganizeStep = () => {
  const { entityRecord, file, displayedImageUrl } = useFileDetailModal();
  const { folderGroups } = useMarketingAssets(entityRecord);
  const { folderTags, toggleTag, suggestedFolderIds } = usePromotePanel();
  const watchTags = usePromotePanel().form.watch("tags");

  const getImageCount = useCallback(
    (tagId: number) => {
      const folderAssets = folderGroups.find((g) => g.tag.id === tagId)?.assets ?? [];
      const currentFileAlreadyCounted =
        file != null && folderAssets.some((a) => String(a.file) === String(file.id));
      return folderAssets.length + (watchTags?.includes(tagId) && !currentFileAlreadyCounted ? 1 : 0);
    },
    [folderGroups, watchTags, file],
  );

  const isAiSuggested = useCallback(
    (tagId: number) => suggestedFolderIds.includes(tagId),
    [suggestedFolderIds],
  );

  const renderExpandedContent = useCallback(
    (tag: MarketingTag) => {
      const folderAssets = folderGroups.find((g) => g.tag.id === tag.id)?.assets ?? [];
      const previewSrc = displayedImageUrl ?? file?.originalImageUrl ?? file?.thumbnailUrl;
      const showCurrentFirst =
        file != null &&
        previewSrc &&
        !folderAssets.some((a) => String(a.file) === String(file.id));

      if (folderAssets.length === 0 && !showCurrentFirst) return null;

      return (
        <div className="px-3 py-3 bg-muted/20">
          <div className="grid grid-cols-3 gap-2">
            {showCurrentFirst ? (
              <div className="aspect-square min-w-0">
                <div className="relative h-full w-full rounded-md overflow-hidden border border-border bg-muted">
                  <img src={previewSrc} alt="" className="h-full w-full object-cover" />
                </div>
              </div>
            ) : null}
            {folderAssets.map((asset) => (
              <div key={asset.id} className="aspect-square min-w-0">
                <AssetThumbnail asset={asset} className="h-full w-full min-h-0 min-w-0" />
              </div>
            ))}
          </div>
        </div>
      );
    },
    [folderGroups, displayedImageUrl, file?.id, file?.thumbnailUrl, file?.originalImageUrl],
  );

  return (
    <div className="space-y-4">
      <MarketingFolderSelector
        headerEnd={
          <PromoteStepHelpTrigger
            stepLabel={PROMOTE_STEPS[2].label}
            helpText={PROMOTE_STEPS[2].helpText}
          />
        }
        folderTags={folderTags}
        selectedTagIds={watchTags ?? []}
        onToggle={toggleTag}
        getImageCount={getImageCount}
        isAiSuggested={isAiSuggested}
        renderExpandedContent={renderExpandedContent}
      />
    </div>
  );
};
